import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SimpleLineChart = (props) => {
  const { device, onChangeFilters } = props;
  const [chartData, setChartData] = useState(device.data);
  const [loading, setLoading] = useState(false);
  // Estados para los filtros
  const [selectedCol, setSelectedCol] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [pageSize, setPageSize] = useState(100);

  const [filteredData, setFilteredData] = useState(chartData);

  // Función para generar colores HSL aleatorios pero visualmente distintos
  const generateColor = (index, total) => {
    // genera un color aleatorios con 6 digitos hexadecimales de tonos oscuros  
    const color = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
    return color;
    // Usamos HSL para mejor control sobre el tono
    const hue = (index * (360 / total)) % 360; // Distribuye los colores uniformemente
    const saturation = 70; // Mantenemos saturación constante
    const lightness = 60; // Mantenemos brillo constante
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Detectamos automáticamente los dispositivos únicos
  const sensors = useMemo(() => {
    if (!device.cols.length) return [];
    return [...new Set(device.cols.map((item) => item.i))];
  }, [device.cols]);

  // Generamos un mapa de colores para cada dispositivo
  const sensorColors = useMemo(() => {
    return sensors.reduce((acc, sensor, index) => {
      acc[sensor] = generateColor(index, sensors.length);
      return acc;
    }, {});
  }, [sensors]);

  // Manejador para cambios en el rango de fechas
  const handleDateRangeChange = (date, field) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: date,
    }));

  };

  // ordena laafas fechas de la data recibida en orden ascendente
  const sortDates = () => {
    chartData.sort((a, b) => new Date(a.f) - new Date(b.f));
  };

  // establece la fecha minima y maxima segun los datos
  const setMinMaxDate = () => {
    const minDate = new Date(chartData[0].f);
    const maxDate = new Date(chartData[chartData.length - 1].f);
    setDateRange({
      startDate: minDate,
      endDate: maxDate,
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.post("/web/devices/home/data", {
        device_id: device._id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        pageSize: pageSize,
        sensor: selectedCol,
      });
      if (response.data.success) {
        console.log(response.data.data);
        // convierte la fecha de texto a fecha
        var data = response.data.data[0];
        data.data.forEach((row) => {
          row.f = new Date(row.f);
        });
        setFilteredData(data.data);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeSensor = (e) => {
    setSelectedCol(e);
  };

  useEffect(() => {
    loadData();
  }, [dateRange.startDate, dateRange.endDate, pageSize, selectedCol]);

  useEffect(() => {
    sortDates();
    setMinMaxDate();
  }, [chartData]);


  return (
    <div className="flex h-full w-full flex-col p-6">
      {/* Filtros */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-white">
            Sensor:
          </label>
          <select
            value={selectedCol}
            onChange={(e) => handleChangeSensor(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-navy-700 dark:text-white lg:w-[180px] xl:w-[180px] 2xl:w-[220px]"
          >
            <option value="">Todos</option>
            {device.cols.map((sensor) => (
              <option key={sensor.i} value={sensor.i}>
                {sensor.n}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-white">
            Desde:
          </label>
          <DatePicker
            selected={dateRange.startDate}
            onChange={(date) => handleDateRangeChange(date, "startDate")}
            showTimeInput
            timeInputLabel="Hora:"
            dateFormat="dd/MM/yyyy HH:mm"
            placeholderText="Fecha inicial"
            className="w-[110px] rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-navy-700 dark:text-white lg:w-[120px] xl:w-[125px] 2xl:w-[160px]"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-white">
            Hasta:
          </label>
          <DatePicker
            selected={dateRange.endDate}
            onChange={(date) => handleDateRangeChange(date, "endDate")}
            showTimeInput
            timeInputLabel="Hora:"
            dateFormat="dd/MM/yyyy HH:mm"
            placeholderText="Fecha final"
            className="w-[110px] rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-navy-700 dark:text-white lg:w-[120px] xl:w-[125px] 2xl:w-[160px]"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-white">
            Mostrar:
          </label>
          <select className="w-[110px] rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-navy-700 dark:text-white lg:w-[120px] xl:w-[125px] 2xl:w-[160px]"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
            <option value="2000">2000</option>
          </select>
          <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-white">
            Registros
          </label>
        </div>

        {loading && <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
        }
      </div>

      {/* Gráfico */}
      <div className="flex-1">
        {filteredData.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center">
            <div className="rounded-lg bg-gray-50 p-8 text-center dark:bg-navy-700">
              <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
                No hay datos disponibles
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {selectedCol || dateRange.startDate || dateRange.endDate
                  ? "Prueba ajustando los filtros para ver más resultados"
                  : "No hay datos para mostrar en este momento"}
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredData}
              margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 30,
              }}
            >
              <CartesianGrid strokeDasharray="4 1 2" />
              <XAxis
                dataKey="f"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  let hours = date.getHours();
                  let minutes = date.getMinutes();
                  hours = hours < 10 ? `0${hours}` : hours;
                  minutes = minutes < 10 ? `0${minutes}` : minutes;
                  return `${hours}:${minutes}`;
                }}
              />
              {selectedCol ? (
                device.cols
                  .filter((sensor) => sensor.i === selectedCol)
                  .map((sensor) => (
                    <YAxis
                      key={sensor.i}
                      yAxisId={sensor.i}
                      orientation="left"
                      domain={["auto", "auto"]}
                      stroke={sensorColors[sensor.i]}
                      tick={{ fontSize: 11 }}
                      label={{ value: sensor.n, angle: -90, position: "insideLeft", fontSize: 11, offset: -5 }}
                    />
                  ))
              ) : (
                device.cols.map((sensor, index) => (
                  <YAxis
                    key={sensor.i}
                    yAxisId={sensor.i}
                    orientation={index % 2 === 0 ? "left" : "right"}
                    domain={["auto", "auto"]}
                    stroke={sensorColors[sensor.i]}
                    tick={{ fontSize: 11 }}
                    width={45}
                  />
                ))
              )}
              <Tooltip
                labelFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.toLocaleString()}`;
                }}
                formatter={(value, name, props) => {
                  return [`${name}: ${value}`];
                }}
              />
              <Legend />
              {
                selectedCol ? (
                  device.cols.filter((sensor) => sensor.i === selectedCol).map((sensor) => (
                    <Line
                      key={sensor.i}
                      type="monotone"
                      dataKey={sensor.i}
                      yAxisId={sensor.i}
                      stroke={sensorColors[sensor.i]}
                      name={`${sensor.n}`}
                      connectNulls
                    />
                  ))
                ) : (
                  device.cols.map((sensor) => (
                    <Line
                      key={sensor.i}
                      type="monotone"
                      dataKey={sensor.i}
                      yAxisId={sensor.i}
                      stroke={sensorColors[sensor.i]}
                      name={`${sensor.n}`}
                      connectNulls
                    />
                  ))
                )
              }
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SimpleLineChart;
